import React, { useState } from 'react'
import {
    Box,
    Button,
    Grid,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import LanguageIcon from '@material-ui/icons/Language'
import StarsIcon from '@material-ui/icons/Stars'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import ViewQuiltIcon from '@material-ui/icons/ViewQuilt'

import { Method } from '../types'
import { TEMPLATES } from '../websiteData'
import { useWebsiteStyles } from '../useWebsiteStyles'

const useStyles = makeStyles((theme: Theme) => ({
    emptyWrapper: {
        marginTop: 0,
    },
    dashedBox: {
        borderRadius: 16,
        border: `2px dashed ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(6),
        textAlign: 'center',
    },
    heroIcon: {
        width: 56,
        height: 56,
        borderRadius: 12,
        backgroundColor: theme.palette.primary.main + '18',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        color: theme.palette.primary.main,
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    cardTitle: {
        fontWeight: 600,
        fontSize: '0.875rem',
    },
    cardBody: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    cardInner: {
        padding: theme.spacing(2.5),
        textAlign: 'left',
    },
    templateBtn: {
        textAlign: 'left',
        padding: theme.spacing(1),
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        background: 'none',
        cursor: 'pointer',
        width: '100%',
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
    },
    swatchRow: {
        display: 'flex',
        gap: 2,
    },
    swatch: {
        height: 16,
        flex: 1,
        borderRadius: 2,
    },
    templateName: {
        fontSize: '0.625rem',
        fontWeight: 500,
        marginTop: 6,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    spacer: {
        height: 52,
    },
}))

interface NoSiteViewProps {
    onPick: (method: Method) => void
}

export const NoSiteView: React.FC<NoSiteViewProps> = ({ onPick }) => {
    const classes = useStyles()
    const sharedClasses = useWebsiteStyles()
    const [prompt, setPrompt] = useState('')
    const [url, setUrl] = useState('')

    return (
        <Box className={classes.emptyWrapper}>
            <Box className={classes.dashedBox}>
                <Box className={classes.heroIcon}>
                    <LanguageIcon />
                </Box>
                <Typography
                    variant="h6"
                    style={{ fontWeight: 600, marginTop: 16 }}
                >
                    Create your leasing website
                </Typography>
                <Typography className={classes.cardBody}>
                    Pick how you want to start. You can swap, edit, and restyle
                    later.
                </Typography>

                <Grid
                    container
                    spacing={2}
                    style={{ marginTop: 32, textAlign: 'left' }}
                >
                    <Grid item xs={12} md={4}>
                        <Box
                            className={`${sharedClasses.card} ${classes.cardInner}`}
                        >
                            <Box className={classes.cardHeader}>
                                <StarsIcon fontSize="small" color="primary" />
                                <Typography className={classes.cardTitle}>
                                    AI Generate
                                </Typography>
                            </Box>
                            <Typography className={classes.cardBody}>
                                Describe your property, get a full site in
                                seconds.
                            </Typography>
                            <TextField
                                multiline
                                rows={3}
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="Modern student housing near UVA with pool, study lounges, fitness center…"
                                style={{ marginTop: 12 }}
                                InputProps={{ style: { fontSize: '0.75rem' } }}
                            />
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => onPick('ai')}
                                style={{
                                    marginTop: 12,
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                }}
                            >
                                Generate site
                            </Button>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box
                            className={`${sharedClasses.card} ${classes.cardInner}`}
                        >
                            <Box className={classes.cardHeader}>
                                <FileCopyIcon
                                    fontSize="small"
                                    color="primary"
                                />
                                <Typography className={classes.cardTitle}>
                                    Clone a site
                                </Typography>
                            </Box>
                            <Typography className={classes.cardBody}>
                                Paste any property&apos;s URL — we&apos;ll match
                                its vibe.
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="https://competitor-housing.com"
                                style={{ marginTop: 12 }}
                                InputProps={{ style: { fontSize: '0.75rem' } }}
                            />
                            <Box className={classes.spacer} />
                            <Button
                                fullWidth
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => onPick('clone')}
                                style={{
                                    marginTop: 12,
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                }}
                            >
                                Clone style
                            </Button>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box
                            className={`${sharedClasses.card} ${classes.cardInner}`}
                        >
                            <Box className={classes.cardHeader}>
                                <ViewQuiltIcon
                                    fontSize="small"
                                    color="primary"
                                />
                                <Typography className={classes.cardTitle}>
                                    Pick a template
                                </Typography>
                            </Box>
                            <Typography className={classes.cardBody}>
                                Pre-designed, ready in one click.
                            </Typography>
                            <Grid
                                container
                                spacing={1}
                                style={{ marginTop: 12 }}
                            >
                                {TEMPLATES.map((t) => (
                                    <Grid item xs={6} key={t.id}>
                                        <button
                                            type="button"
                                            className={classes.templateBtn}
                                            onClick={() => onPick('template')}
                                        >
                                            <Box className={classes.swatchRow}>
                                                {t.swatch.map((c, i) => (
                                                    <Box
                                                        key={i}
                                                        className={
                                                            classes.swatch
                                                        }
                                                        style={{
                                                            backgroundColor: c,
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                            <Typography
                                                className={classes.templateName}
                                            >
                                                {t.name}
                                            </Typography>
                                        </button>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    )
}
